"use client";
// Imports
import { Container, ComponentWrapper, Img } from "@/components";
import Marquee from "react-fast-marquee";
import Link from "next/link";
import { resolveLink } from "@/lib/utils";

function sectionHeading(text) {
  if (!text) return null;
  return (
    <h2 className="font-heading font-heading-sentence text-brand-fawn text-md lg:text-xl leading-none text-center">
      {text}
    </h2>
  );
}

function FeaturedLogo({ image, link, title }) {
  if (!image) return null;

  const href = resolveLink(link);
  const logo = (
    <Img
      data={image}
      alt={image?.alt || title || "Naming partner"}
      className="object-contain w-auto h-auto max-h-48 sm:max-h-52 lg:max-h-56 max-w-[min(44rem,100%)] sm:max-w-[min(48rem,100%)] lg:max-w-[min(52rem,100%)] mx-auto"
    />
  );

  if (!href) {
    return <div className="flex justify-center px-4">{logo}</div>;
  }

  const isExternal = href.includes("http");
  if (isExternal) {
    return (
      <div className="flex justify-center px-4">
        <a href={href} target="_blank" rel="noopener noreferrer">
          {logo}
        </a>
      </div>
    );
  }

  return (
    <div className="flex justify-center px-4">
      <Link href={href}>{logo}</Link>
    </div>
  );
}

// Rolling Carousel — optional naming partner + supporting sponsor marquee
export function RollingCarousel({
  type,
  title,
  items = [],
  featuredTitle,
  featuredImage,
  featuredLink,
}) {
  const sponsors = items?.filter((item) => item?.image) || [];
  const hasFeatured = Boolean(featuredImage);
  const hasCarousel = sponsors.length > 0;

  if (!hasFeatured && !hasCarousel) return null;

  return (
    <ComponentWrapper type={type}>
      <Container width="full" padding={false}>
        <div className="flex flex-col items-center justify-center gap-8 sm:gap-10 xl:gap-14 overflow-x-hidden">
          {hasFeatured && (
            <div className="flex flex-col items-center gap-5 sm:gap-6 xl:gap-8 w-full">
              {sectionHeading(featuredTitle)}
              <FeaturedLogo
                image={featuredImage}
                link={featuredLink}
                title={featuredTitle}
              />
            </div>
          )}

          {hasCarousel && (
            <div className="flex flex-col items-center gap-10 xl:gap-14 w-full">
              {sectionHeading(title)}
              <Marquee gradient={false} speed={100} autoFill={true}>
                {sponsors.map((item, index) => {
                  const href = resolveLink(item.sponsor_link);
                  const logo = (
                    <Img
                      data={item.image}
                      alt={item.image?.alt || item.title || "Sponsor"}
                      className="object-contain w-auto h-auto max-h-16 max-w-[200px] mx-auto"
                    />
                  );

                  return (
                    <div
                      key={item?.id ?? index}
                      className="mx-5 xl:mx-10 w-full max-w-[200px] animate-card-in"
                      style={{
                        "--anim-delay": `${Math.min(index, 12) * 40}ms`,
                      }}
                    >
                      {href ? (
                        <Link href={href} target="_blank" rel="noopener noreferrer">
                          {logo}
                        </Link>
                      ) : (
                        logo
                      )}
                    </div>
                  );
                })}
              </Marquee>
            </div>
          )}
        </div>
      </Container>
    </ComponentWrapper>
  );
}
